import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { format, isSameMonth, parseISO } from 'date-fns';

import en from 'date-fns/locale/en-US';
import de from 'date-fns/locale/de';
import Icon from '../../../components/icon/Icon';
import BlockedSlot from '../../../core/types/model/BlockedSlot';
import { DATE_FORMAT_DATEPICKER_LONG, DATE_FORMAT_DATEPICKER_SHORT } from '../../../core/constants/date';

import 'react-datepicker/dist/react-datepicker.css';
import './BlockBookingSlots.scss';

interface Props {
  blockedSlots: BlockedSlot[];
  disabled: boolean;
  handleDateChange: (date: Date) => void;
}

interface HighlightDates {
  [className: string]: Date[];
}

const BlockBookingSlotsCalendar: React.FC<Props> = ({ blockedSlots, disabled, handleDateChange }) => {
  const { t, i18n } = useTranslation();
  registerLocale('de-DE', de);
  registerLocale('en-EN', en);

  const getBlockedDays = useMemo(
    () => blockedSlots.filter((s) => s.blockedAllDay).map((slot) => new Date(format(parseISO(slot.blockedDay), DATE_FORMAT_DATEPICKER_LONG))),
    [blockedSlots]
  );

  const getHighlightedDates = useMemo(() => {
    return [
      {
        'react-datepicker__day--highlighted-custom-1': blockedSlots
          .filter((s) => s.blockedAllDay)
          .map((slot) => new Date(format(parseISO(slot.blockedDay), DATE_FORMAT_DATEPICKER_LONG)))
      },
      {
        'react-datepicker__day--highlighted-custom-2': blockedSlots
          .filter((s) => !s.blockedAllDay)
          .map((slot) => new Date(format(parseISO(slot.blockedDay), DATE_FORMAT_DATEPICKER_LONG)))
      }
    ] as Array<HighlightDates | Date> | undefined;
  }, [blockedSlots]);
  return (
    <DatePicker
      calendarClassName={`block-booking-slots__calendar ${disabled ? 'block-booking-slots__calendar--disabled' : ''}`}
      renderCustomHeader={({
        monthDate,
        customHeaderCount,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        changeMonth,
        changeYear
      }) => (
        <div
          className={`block-booking-slots__calendar-header ${
            customHeaderCount === 0 && isSameMonth(new Date(), monthDate) ? 'block-booking-slots__calendar-header--active' : ''
          }`}
        >
          <button
            disabled={prevMonthButtonDisabled}
            className="react-datepicker__navigation react-datepicker__navigation--previous"
            style={customHeaderCount === 1 || customHeaderCount === 2 ? { visibility: 'hidden' } : undefined}
            onClick={() => {
              decreaseMonth();
              decreaseMonth();
            }}
          >
            <Icon className="block-booking-slots__calendar-arrow-left" icon="arrow" size="small" svg />
          </button>
          <span className="react-datepicker__current-month">
            {monthDate.toLocaleString(i18n.language, {
              month: 'long',
              year: 'numeric'
            })}
          </span>
          <button
            disabled={nextMonthButtonDisabled}
            className="react-datepicker__navigation react-datepicker__navigation--next"
            style={customHeaderCount !== 0 ? { visibility: 'hidden' } : undefined}
            onClick={() => {
              increaseMonth();
              increaseMonth();
            }}
          >
            <Icon className="block-booking-slots__calendar-arrow-right" icon="arrow" size="small" svg />
          </button>
          {customHeaderCount === 0 && (
            <button
              onClick={() => {
                changeYear(new Date().getFullYear());
                changeMonth(new Date().getMonth());
              }}
              className="react-datepicker__today-button"
            >
              {t('common.today')}
            </button>
          )}
        </div>
      )}
      excludeDates={getBlockedDays}
      focusSelectedMonth={false}
      highlightDates={getHighlightedDates}
      dateFormat={DATE_FORMAT_DATEPICKER_SHORT}
      openToDate={undefined}
      calendarStartDay={1}
      showPreviousMonths={false}
      minDate={new Date()}
      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
      disabledKeyboardNavigation
      locale={i18n.language}
      formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 1)}
      inline
      monthsShown={3}
      onChange={(date: Date) => handleDateChange(date)}
    />
  );
};

export default BlockBookingSlotsCalendar;
